import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIONS = ["", "set_plan", "change_plan", "suspend", "reactivate", "impersonate", "exit_impersonate"];

export default function AdminAuditLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const limit = 50;
  const [action, setAction] = useState("");
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit });
      if (action) params.set("action", action);
      if (userId) params.set("user_id", userId);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      const { data } = await axios.get(`${API}/admin/audit-log?${params}`, { withCredentials: true });
      setItems(data.items); setTotal(data.total);
    } catch { toast.error("Errore"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, action]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="admin-audit-page">
      <div>
        <h1 className="text-3xl font-bold text-[#0D1F3C]">Console Admin · Audit Log</h1>
        <p className="text-sm text-[#0D1F3C]/60 mt-1">{total} eventi totali</p>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Azione</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger><SelectValue placeholder="Tutte le azioni" /></SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => <SelectItem key={a || "all"} value={a || "all"}>{a || "Tutte"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">User ID</label>
          <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="Mongo ObjectId..." />
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">Da</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-medium text-[#0D1F3C]/60 mb-1 block">A</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <Button onClick={() => { setPage(1); load(); }}>Filtra</Button>
      </div>
      <div className="bg-white border border-[#0D1F3C]/8 rounded-xl overflow-hidden">
        <div className="max-h-[calc(100vh-340px)] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0D1F3C] text-white text-left sticky top-0">
              <tr>
                <th className="px-4 py-3">Quando</th>
                <th className="px-4 py-3">Azione</th>
                <th className="px-4 py-3">Admin</th>
                <th className="px-4 py-3">Target</th>
                <th className="px-4 py-3">Payload</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={5} className="text-center py-8 text-[#0D1F3C]/40">Caricamento…</td></tr> :
               items.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-[#0D1F3C]/40">Nessun evento</td></tr> :
               items.map((a, i) => (
                <tr key={i} className="border-t border-[#0D1F3C]/5 hover:bg-[#F4F7FB]">
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/70 whitespace-nowrap">
                    {new Date(a.at).toLocaleString("it-IT")}
                  </td>
                  <td className="px-4 py-3"><Badge variant="outline">{a.action}</Badge></td>
                  <td className="px-4 py-3 text-xs">{a.admin_email || "—"}</td>
                  <td className="px-4 py-3 text-xs">
                    {a.payload?.target_email || (a.target_user_id ? a.target_user_id.slice(0, 12) + "…" : "—")}
                  </td>
                  <td className="px-4 py-3 text-xs text-[#0D1F3C]/70 font-mono">
                    {a.payload?.from && a.payload?.to ? `${a.payload.from} → ${a.payload.to}` :
                     a.payload?.reason ? a.payload.reason :
                     a.payload?.plan_code ? `plan=${a.payload.plan_code}` :
                     JSON.stringify(a.payload || {}).slice(0, 80)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-[#F4F7FB] border-t border-[#0D1F3C]/5">
          <span className="text-xs text-[#0D1F3C]/60">{total} eventi</span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}><CaretLeft size={14} /></Button>
            <span className="text-sm font-medium">{page} / {totalPages}</span>
            <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}><CaretRight size={14} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}
